<template>
  <Dialog
    v-if="modelValue"
    title="Issue customer credit"
    :custom-title="true"
    :show-close-button="true"
    :show-footer="false"
    :lg="false"
    :md="true"
    :sm="false"
    custom-width-class="!max-w-[min(32rem,96vw)]"
    :loading="creditSubmitLoading"
    @close-event="close"
  >
    <template #title>
      <div class="flex min-w-0 flex-col gap-0.5 pr-2">
        <h4 class="text-lg font-semibold leading-tight text-ink">
          Issue customer credit
        </h4>
        <p class="text-sm font-medium text-ink-soft">
          Order #{{ orderLabel }}
        </p>
      </div>
    </template>
    <template #modal-content>
      <div
        class="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm leading-relaxed text-amber-950"
        role="note"
      >
        <p class="mb-2 font-medium text-amber-950">How Stripe applies this credit</p>
        <p class="text-amber-950/95">
          This uses a
          <a
            href="https://docs.stripe.com/invoicing/dashboard/credit-notes"
            target="_blank"
            rel="noopener noreferrer"
            class="font-semibold text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-strong)]"
          >Stripe credit note</a>, not a card refund. What happens depends on the invoice:
        </p>
        <ul class="mt-2 list-inside list-disc space-y-1.5 pl-0.5 text-amber-950/95">
          <li>
            <span class="font-semibold">Invoice not fully paid</span>
            — the amount reduces what the customer still owes on
            <em>that</em> invoice.
          </li>
          <li>
            <span class="font-semibold">Invoice already paid in full</span>
            — the amount is added to the customer’s
            <span class="font-semibold">Stripe balance</span> and can apply to a
            <em>later</em> invoice.
          </li>
        </ul>
      </div>

      <section v-if="credits.length" class="mb-6">
        <h5 class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <span class="inline-block h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          Credit history
        </h5>
        <div class="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/80">
          <table class="w-full border-collapse text-left text-xs">
            <thead class="sticky top-0 bg-slate-100/95 text-[10px] font-semibold uppercase text-slate-600">
              <tr>
                <th class="px-2.5 py-2">Amount</th>
                <th class="px-2.5 py-2">Reason</th>
                <th class="px-2.5 py-2">When</th>
                <th class="px-2.5 py-2 text-right">Stripe</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 bg-surface">
              <tr v-for="c in credits" :key="'credit-row-' + String(c.id)">
                <td class="whitespace-nowrap px-2.5 py-2 font-semibold text-slate-900">
                  {{ formatMoney(Number(c.amount)) }}
                  {{ c.currency }}
                </td>
                <td class="max-w-[10rem] truncate px-2.5 py-2 text-slate-700" :title="String(c.reason ?? '')">
                  {{ c.reason || '—' }}
                </td>
                <td class="whitespace-nowrap px-2.5 py-2 text-slate-500">
                  {{ formatListedAt(c.created_at) }}
                </td>
                <td class="px-2.5 py-2 text-right">
                  <a
                    v-if="stripeDashboardUrl && c.stripe_credit_note_id"
                    :href="`${stripeDashboardUrl}/credit_notes/${c.stripe_credit_note_id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-medium text-[var(--accent)] underline underline-offset-2"
                    >Open</a>
                  <span v-else class="text-slate-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="creditableInvoices.length" class="space-y-4">
        <h5 class="text-xs font-semibold uppercase tracking-wide text-slate-600">
          New credit
        </h5>
        <label class="flex flex-col gap-1.5 text-sm text-slate-800">
          <span class="font-medium">Link to partial invoice</span>
          <select
            v-model.number="form.order_item_invoice_id"
            class="rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm shadow-sm outline-none ring-slate-200 focus:border-[var(--accent)] focus:ring-2"
          >
            <option :value="0">Choose an invoice…</option>
            <option
              v-for="inv in creditableInvoices"
              :key="'cred-opt-' + String(inv.id ?? inv.stripe_invoice_id)"
              :value="Number(inv.id)"
            >
              {{ inv.stripe_invoice_number || inv.stripe_invoice_id }}
              <template v-if="inv.status"> · {{ inv.status }}</template>
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1.5 text-sm text-slate-800">
          <span class="font-medium">Amount</span>
          <input
            v-model="form.amount"
            type="number"
            min="0"
            step="0.01"
            class="rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm shadow-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-slate-200"
            placeholder="0.00"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-sm text-slate-800">
          <span class="font-medium">Reason</span>
          <textarea
            v-model="form.reason"
            rows="3"
            class="rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm shadow-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-slate-200"
            placeholder="Required — e.g. goodwill, pricing adjustment"
          />
        </label>
        <div class="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            class="inline-flex items-center rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--accent-strong)] disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="
              creditSubmitLoading ||
              !form.order_item_invoice_id ||
              !form.amount ||
              !String(form.reason || '').trim()
            "
            @click="submit"
          >
            <span
              v-if="creditSubmitLoading"
              class="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"
            />
            Issue credit
          </button>
          <button
            type="button"
            class="rounded-lg border border-slate-300 bg-surface px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="close"
          >
            Cancel
          </button>
        </div>
      </section>

      <div
        v-else
        class="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600"
      >
        <template v-if="partialInvoices.length">
          A sync is still in progress or awaiting reconciliation — credit is only available once an invoice is
          confirmed.
        </template>
        <template v-else>
          No partial Stripe invoices are on file for this order yet. Create an invoice first, then you can issue
          account credit against it.
        </template>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { computed, reactive, ref, watch } from 'vue';

const props = defineProps<{
  orderId: number;
  orderLabel: string;
  partialInvoices: Record<string, any>[];
  credits: Record<string, any>[];
}>();

/** Bug 20222 — a pre-finalize `pending_stripe_sync` row has no confirmed Stripe invoice yet; crediting it is meaningless. */
function isPendingStripeSyncStatus(status: unknown): boolean {
  return String(status ?? '').trim().toLowerCase() === 'pending_stripe_sync';
}

/** Selectable invoices for a new credit — excludes rows still awaiting sync/reconciliation. */
const creditableInvoices = computed(() =>
  props.partialInvoices.filter((inv) => !isPendingStripeSyncStatus(inv?.status)),
);

const modelValue = defineModel<boolean>({ default: false });

const stripeDashboardUrl = import.meta.env.VITE_APP_STRIPE_DASHBOARD_URL as string | undefined;

const creditSubmitLoading = ref(false);
const form = reactive({
  order_item_invoice_id: 0,
  amount: '',
  reason: '',
});

function resetForm(): void {
  form.order_item_invoice_id = 0;
  form.amount = '';
  form.reason = '';
}

watch(modelValue, (open) => {
  if (open) {
    resetForm();
  }
});

const emit = defineEmits<{
  creditIssued: [row: Record<string, unknown>];
}>();

function formatMoney(value: string | number): string {
  if (value === null || value === undefined || value === '') return '0.00';
  const normalized = String(value).replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
}

function formatListedAt(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function close(): void {
  modelValue.value = false;
}

function submit(): void {
  const oid = Number(form.order_item_invoice_id);
  if (!oid || !props.orderId) {
    showToast('Select an invoice.', 'error');
    return;
  }
  const amt = parseFloat(String(form.amount));
  if (!(amt > 0)) {
    showToast('Enter a valid amount.', 'error');
    return;
  }
  const reason = String(form.reason || '').trim();
  if (reason.length < 3) {
    showToast('Reason must be at least 3 characters.', 'error');
    return;
  }
  creditSubmitLoading.value = true;
  http
    .post(`customer-orders/${props.orderId}/stripe-account-credit`, {
      order_item_invoice_id: oid,
      amount: amt,
      reason,
      idempotency_key: crypto.randomUUID(),
    })
    .then((response: any) => {
      const row = response?.data?.result?.credit;
      if (row) {
        emit('creditIssued', row);
        resetForm();
        showToast('Account credit issued');
        close();
      }
    })
    .catch((error) => {
      console.error('stripe account credit error', error);
      handleResponseException(error);
    })
    .finally(() => {
      creditSubmitLoading.value = false;
    });
}
</script>
